import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArtButton, useIdpService } from "@art/shared-ui";
import { RootState } from "../../../../store";
import { useAttributePermission } from "../../../permissions";
import { buildAbacBulkUpdatePayload } from "./abac-bulk-payload";
import type { RoleDetailContext } from "./index";

interface AbacAttribute {
  description: string;
  id: string;
  name: string;
  title?: string;
  category_id: string;
  category_name: string;
  category_title?: string;
  isSelected: boolean;
}

interface AbacFeature {
  id: string;
  code: string;
  name: string;
  title?: string;
  attributes: AbacAttribute[];
  description: string;
}

interface AbacApplication {
  id: string;
  code: string;
  name: string;
  title?: string;
  features: AbacFeature[];
}

type Feedback = { kind: "success" | "error" | "info"; message: string } | null;

const EMPTY_SET: ReadonlySet<string> = new Set();

interface FeatureCardProps {
  feat: AbacFeature;
  categories: Array<{ key: string; title: string }>;
  selectedSet: ReadonlySet<string>;
  canWrite: boolean;
  onToggleAttribute: (featureId: string, attrId: string) => void;
  onSetFeatureAll: (feat: AbacFeature, checked: boolean) => void;
}

const FeatureCard = memo(function FeatureCard({
  feat,
  categories,
  selectedSet,
  canWrite,
  onToggleAttribute,
  onSetFeatureAll,
}: FeatureCardProps) {
  const total = feat.attributes.length;
  let selectedCount = 0;
  for (const a of feat.attributes) if (selectedSet.has(a.id)) selectedCount += 1;
  const allChecked = total > 0 && selectedCount === total;
  const indeterminate = selectedCount > 0 && selectedCount < total;

  // Group attributes by their category name
  const attributesByCategory = useMemo(() => {
    const map: Record<string, AbacAttribute[]> = {};
    feat.attributes.forEach((attr) => {
      const catName = attr.category_name;
      if (!map[catName]) map[catName] = [];
      map[catName].push(attr);
    });
    return map;
  }, [feat.attributes]);

  return (
    <Box sx={{ px: 2.5, py: 2.25 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 2 }}>
        <Checkbox
          size="small"
          checked={allChecked}
          indeterminate={indeterminate}
          disabled={!canWrite || total === 0}
          onChange={(e) => onSetFeatureAll(feat, e.target.checked)}
          sx={{ p: 0, mt: 0.25, width: 24, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700}>
            {feat.title || feat.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {feat.description || "Choose the actions this role can perform."}
          </Typography>
        </Box>
        <Chip
          size="small"
          variant={selectedCount > 0 ? "filled" : "outlined"}
          color={selectedCount > 0 ? "primary" : "default"}
          label={`${selectedCount}/${total}`}
          sx={{ height: 24, fontWeight: 700 }}
        />
      </Stack>

      {total === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ pl: 4, fontStyle: 'italic' }}>
          No attributes configured for this feature.
        </Typography>
      ) : (
          <Box sx={{ pl: { xs: 0, sm: 4.5 } }}>
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", sm: "row" }, 
              flexWrap: "wrap", 
              gap: 4 
            }}
          >
            {categories.map((category) => {
              const attrsInCat = attributesByCategory[category.key] || [];
              if (attrsInCat.length === 0) return null;

              return (
                <Box 
                  key={category.key}
                  sx={{ 
                    minWidth: { xs: "100%", sm: "200px", md: "240px" }, 
                    flex: "1 1 0px"
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ display: "block", mb: 1 }}
                  >
                    {category.title}
                  </Typography>

                  <Stack spacing={0.25}>
                    {attrsInCat.map((attr) => (
                      <Stack
                        key={attr.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          minHeight: 36,
                          px: 0.5,
                          py: 0.5,
                          bgcolor: selectedSet.has(attr.id) ? "primary.50" : "transparent",
                          borderRadius: 1,
                          transition: "background-color 160ms ease",
                          '&:hover': { bgcolor: "action.hover" },
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedSet.has(attr.id)}
                          inputProps={{ 'aria-label': attr.title || attr.name }}
                          disabled={!canWrite}
                          onChange={() => onToggleAttribute(feat.id, attr.id)}
                          sx={{ p: 0, width: 24, flexShrink: 0 }}
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.85rem', 
                            lineHeight: 1.4,
                            wordBreak: "break-word" 
                          }}
                        >
                          {attr.title || attr.name}
                        </Typography>
                        <Tooltip title={attr.description} arrow describeChild>
                          <IconButton size="small" aria-label={`About ${attr.title || attr.name}`} sx={{ color: "text.secondary", flexShrink: 0 }}>
                            <InfoOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
});

const RoleAbac = () => {
  const { role, canManageRole } = useOutletContext<RoleDetailContext>();
  const { id: roleId } = useParams<{ id: string }>();
  const idpService = useIdpService();
  const tenantName = useSelector((state: RootState) => state.auth.tenantName);
  const hasWritePermission = useAttributePermission("PLATFORM_SETTINGS", "ROLE_MANAGEMENT", "ManageRolePermissions");
  const canWrite = canManageRole && hasWritePermission;

  const [applications, setApplications] = useState<AbacApplication[]>([]);
  // Per-feature selection: featureId -> Set of attributeIds
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [initialSelected, setInitialSelected] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedRecently, setSavedRecently] = useState(false);
  const [saveBlocked, setSaveBlocked] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") ?? "";
  const selectedAppId = searchParams.get("app") ?? "";
  const updateParam = useCallback(
    (key: string, value: string) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const setSearch = useCallback((value: string) => updateParam("q", value), [updateParam]);
  const setSelectedAppId = useCallback((value: string) => updateParam("app", value), [updateParam]);
  const loadVersion = useRef(0);
  const savingRequest = useRef(false);
  // The app in the URL when a load starts, so it can be restored after a refresh.
  const preferredAppId = useRef(selectedAppId);
  preferredAppId.current = selectedAppId;

  const loadTree = async () => {
    if (!tenantName || !roleId) return;
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError(null);
    setFeedback(null);
    setApplications([]);
    setSelected({});
    setInitialSelected({});
    const preferred = preferredAppId.current;
    try {
      const res = await idpService.get(
        `api/tenants/${tenantName}/roles/${roleId}/abac`,
      );
      if (version !== loadVersion.current) return;
      const apps = sortAbacCatalog(res?.data?.data?.applications || []);
      setApplications(apps);
      if (apps.length > 0) {
        setSelectedAppId(
          apps.find((app) => app.id === preferred)?.id ?? apps[0].id,
        );
      }
      
      const init: Record<string, Set<string>> = {};
      apps.forEach((app) =>
        app.features.forEach((feat) => {
          const set = new Set<string>();
          feat.attributes.forEach((attr) => {
            if (attr.isSelected) set.add(attr.id);
          });
          init[feat.id] = set;
        }),
      );
      setSelected(cloneRecordSet(init));
      setInitialSelected(init);
    } catch (err) {
      if (version !== loadVersion.current) return;
      console.error(err);
      setApplications([]);
      setLoadError("Unable to load permissions. Please try again.");
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadTree();
    return () => {
      loadVersion.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantName, roleId]);

  // --- Derived state ---

  const unsavedFeatureIds = useMemo(() => {
    const unsaved = new Set<string>();
    const allFeatureIds = new Set([
      ...Object.keys(selected),
      ...Object.keys(initialSelected),
    ]);
    allFeatureIds.forEach((fid) => {
      const cur = selected[fid] || new Set<string>();
      const ini = initialSelected[fid] || new Set<string>();
      if (cur.size !== ini.size) {
        unsaved.add(fid);
        return;
      }
      for (const id of cur) {
        if (!ini.has(id)) {
          unsaved.add(fid);
          return;
        }
      }
    });
    return unsaved;
  }, [selected, initialSelected]);

  const hasUnsavedChanges = unsavedFeatureIds.size > 0;

  const filteredApps = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return applications;
    return applications
      .map((app) => {
        const features = app.features
          .map((feat) => {
            const attributes = feat.attributes.filter(
              (a) =>
                (a.title || a.name).toLowerCase().includes(q) ||
                (a.category_title || a.category_name).toLowerCase().includes(q),
            );
            const featMatches =
              (feat.title || feat.name).toLowerCase().includes(q) ||
              feat.code.toLowerCase().includes(q);
            return featMatches || attributes.length > 0
              ? { ...feat, attributes: featMatches ? feat.attributes : attributes }
              : null;
          })
          .filter(Boolean) as AbacFeature[];
        const appMatches =
          (app.title || app.name).toLowerCase().includes(q) ||
          app.code.toLowerCase().includes(q);
        return appMatches || features.length > 0
          ? { ...app, features: appMatches ? app.features : features }
          : null;
      })
      .filter(Boolean) as AbacApplication[];
  }, [applications, search]);
  
  useEffect(() => {
     if (filteredApps.length > 0 && !filteredApps.find((a) => a.id === selectedAppId)) {
         setSelectedAppId(filteredApps[0].id);
     }
  }, [filteredApps, selectedAppId]);

  const handleSave = async (nextSelection = selected, featuresToSave = Array.from(unsavedFeatureIds)) => {
    if (!canWrite || !tenantName || !roleId || savingRequest.current || featuresToSave.length === 0) return;
    savingRequest.current = true;
    const version = loadVersion.current;
    setSaving(true);
    setSavedRecently(false);
    setSaveBlocked(false);
    setFeedback(null);

    const selectionsToSave: Record<string, Set<string>> = {};
    featuresToSave.forEach((featureId) => {
      selectionsToSave[featureId] = new Set(nextSelection[featureId] || []);
    });
    try {
      await new Promise<void>((resolve, reject) => {
        idpService.saveData(`api/tenants/${tenantName}/roles/${roleId}/abac/bulk`, "put", {
          onSuccess: () => resolve(),
          onError: (err: unknown) => reject(err),
        })(buildAbacBulkUpdatePayload(featuresToSave, selectionsToSave));
      });

      if (version !== loadVersion.current) return;
      setInitialSelected((prev) => {
        const next = { ...prev };
        featuresToSave.forEach((fid) => {
          next[fid] = new Set(selectionsToSave[fid]);
        });
        return next;
      });
      setSavedRecently(true);
    } catch (err) {
      console.error(err);
      if (version !== loadVersion.current) return;
      setSaveBlocked(true);
      setFeedback({
        kind: "error",
        message: "The permission update could not be confirmed. Retry to save your selected permissions.",
      });
    } finally {
      savingRequest.current = false;
      setSaving(false);
    }
  };

  // Dispatch from the user's action, so route unmount cannot cancel a queued
  // permission change. Keep one request in flight to preserve write ordering.
  const commitSelection = (next: Record<string, Set<string>>, changed: string[]) => {
    if (!canWrite || loading || savingRequest.current) return;
    setSelected(next);
    void handleSave(next, Array.from(new Set([...unsavedFeatureIds, ...changed])));
  };
  const toggleAttribute = (featureId: string, attrId: string) => {
    const next = cloneRecordSet(selected);
    const values = next[featureId] || new Set<string>();
    if (values.has(attrId)) values.delete(attrId);
    else values.add(attrId);
    next[featureId] = values;
    commitSelection(next, [featureId]);
  };
  const setFeatureAll = (feature: AbacFeature, checked: boolean) => {
    const next = cloneRecordSet(selected);
    next[feature.id] = new Set(checked ? feature.attributes.map(attribute => attribute.id) : []);
    commitSelection(next, [feature.id]);
  };
  const setApplicationAll = (application: AbacApplication, checked: boolean) => {
    const next = cloneRecordSet(selected);
    application.features.forEach(feature => {
      next[feature.id] = new Set(checked ? feature.attributes.map(attribute => attribute.id) : []);
    });
    commitSelection(next, application.features.map(feature => feature.id));
  };

  useEffect(() => {
    if (!saving && !hasUnsavedChanges) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [saving, hasUnsavedChanges]);

  useEffect(() => {
    if (!savedRecently) return;
    const timer = window.setTimeout(() => setSavedRecently(false), 1800);
    return () => window.clearTimeout(timer);
  }, [savedRecently]);

  // --- Render ---

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }} spacing={2}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading access catalog…
        </Typography>
      </Stack>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity="error"
        action={<ArtButton label="Retry" variant="text" size="small" fullWidth={false} onClick={() => void loadTree()} />}
      >
        {loadError}
      </Alert>
    );
  }

  if (applications.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No applications configured yet. Ask a superadmin to set up applications and features in IDP.
      </Alert>
    );
  }

  const activeApp = filteredApps.find((a) => a.id === selectedAppId);

  const activeAppCategories = (() => {
    if (!activeApp) return [];
    const uniqueCats = new Map<string, string>();
    activeApp.features.forEach((feat) => {
      feat.attributes.forEach((attr) => {
        if (attr.category_name) {
          uniqueCats.set(attr.category_name, attr.category_title || attr.category_name);
        }
      });
    });
    return Array.from(uniqueCats, ([key, title]) => ({ key, title })).sort((a, b) =>
      a.title.localeCompare(b.title),
    );
  })();

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
            size="small"
            placeholder="Search permissions"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 360 },
              bgcolor: "background.paper",
              borderRadius: 1.5,
              "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
        />
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minHeight: 24 }}>
          {saving && <CircularProgress size={16} />}
          {savedRecently && !saving && <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18 }} />}
          <Typography variant="caption" color={saveBlocked ? "error.main" : "text.secondary"}>
            {saving
              ? "Saving changes…"
              : saveBlocked
                ? "Couldn’t save changes"
                : savedRecently
                  ? "All changes saved"
                  : "Changes save automatically"}
          </Typography>
        </Stack>
      </Stack>

      {/* Read-only banner — when user lacks Write permission */}
      {!canWrite && (
        <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
          {role?.is_default
            ? "This default role is system-defined. Its permissions can be reviewed but not changed from the console."
            : "You have read-only access to role permissions. Contact an administrator to make changes."}
        </Alert>
      )}

      {/* Inline feedback */}
      {feedback && (
        <Alert
          severity={feedback.kind}
          action={saveBlocked && canWrite ? <ArtButton label="Retry" variant="text" size="small" fullWidth={false} disabled={saving} onClick={() => void handleSave()} /> : undefined}
          sx={{ mb: 3 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      {/* Empty search */}
      {search && filteredApps.length === 0 && (
        <Card variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No permissions match "{search}"
          </Typography>
          <ArtButton
            buttonType="button"
            variant="outlined"
            label="Clear search"
            onClick={() => setSearch("")}
          />
        </Card>
      )}

      {filteredApps.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'flex-start' }}>
          
          {/* LEFT PANE: Application Tabs */}
          <Paper 
            variant="outlined" 
            sx={{ 
               width: { xs: '100%', md: 280 },
               flexShrink: 0, 
               borderRadius: 2.5,
               position: { md: 'sticky' },
               top: { md: 16 },
               bgcolor: 'background.paper',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ px: 2.25, py: 2, borderBottom: 1, borderColor: "divider", bgcolor: "action.hover" }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                Applications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a product area
              </Typography>
            </Box>
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={selectedAppId}
              onChange={(_, val) => setSelectedAppId(val)}
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{ 
                 borderBottom: { xs: 1, md: 0 }, 
                 borderColor: 'divider', 
                 minHeight: { md: 300 },
                 '& .MuiTabs-flexContainer': {
                   borderRight: 0,
                 }
              }}
            >
              {filteredApps.map((app) => {
                 const isSelected = selectedAppId === app.id;
                 return (
                   <Tab 
                     key={app.id} 
                     value={app.id} 
                     label={
                       <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1.25 }}>
                         <Box
                           sx={{
                             width: 34,
                             height: 34,
                             borderRadius: 1.25,
                             display: "grid",
                             placeItems: "center",
                             flexShrink: 0,
                             bgcolor: isSelected ? "primary.main" : "action.hover",
                             color: isSelected ? "primary.contrastText" : "text.secondary",
                             fontSize: 12,
                             fontWeight: 800,
                           }}
                         >
                           {(app.title || app.name).split(/\s+/).slice(0, 2).map((word) => word[0]).join("")}
                         </Box>
                         <Box sx={{ textAlign: 'left', flex: 1, overflow: 'hidden' }}>
                           <Typography variant="subtitle2" noWrap sx={{ fontWeight: isSelected ? 700 : 500 }}>
                             {app.title || app.name}
                           </Typography>
                           <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                             {app.features.length} permission group{app.features.length === 1 ? "" : "s"}
                           </Typography>
                         </Box>
                       </Box>
                     } 
                     sx={{ 
                        alignItems: 'flex-start', 
                        py: 2, 
                        px: 2,
                        textTransform: 'none',
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: isSelected ? 'primary.50' : 'transparent',
                        borderLeft: 3,
                        borderLeftColor: isSelected ? "primary.main" : "transparent",
                        '&:hover': { bgcolor: isSelected ? 'primary.50' : 'action.hover' },
                        '&:last-child': { borderBottom: 0 }
                     }}
                   />
                 );
              })}
            </Tabs>
          </Paper>

          {/* RIGHT PANE: Features Layout */}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {activeApp && (
              <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                {/* Header with App Select All */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    background: "linear-gradient(90deg, rgba(0,145,255,0.08), rgba(255,255,255,0))",
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                    <Checkbox
                      checked={activeApp.features.length > 0 && activeApp.features.every((f) => 
                          (selected[f.id] || new Set()).size === f.attributes.length && f.attributes.length > 0
                      )}
                      indeterminate={
                        activeApp.features.some((f) => (selected[f.id] || new Set()).size > 0) &&
                        !activeApp.features.every((f) => (selected[f.id] || new Set()).size === f.attributes.length && f.attributes.length > 0)
                      }
                      disabled={!canWrite || saving}
                      onChange={(e) => setApplicationAll(activeApp, e.target.checked)}
                      sx={{ p: 0, mt: 0.25, width: 24, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                        {activeApp.title || activeApp.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enable all permissions for this application, or choose individual actions below.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Permission groups */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
                    alignItems: "start",
                  }}
                >
                  {activeApp.features.map((feat, index) => (
                    <Box
                      key={feat.id}
                      sx={{
                        borderBottom: 1,
                        borderRight: { xl: index % 2 === 0 ? 1 : 0 },
                        borderColor: "divider",
                      }}
                    >
                      <FeatureCard
                        feat={feat}
                        categories={activeAppCategories}
                        selectedSet={selected[feat.id] || EMPTY_SET}
                        canWrite={canWrite && !saving}
                        onToggleAttribute={toggleAttribute}
                        onSetFeatureAll={setFeatureAll}
                      />
                    </Box>
                  ))}
                </Box>

                {activeApp.features.length === 0 && (
                   <Box sx={{ p: 4, textAlign: 'center' }}>
                     <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                       No features found for this application.
                     </Typography>
                   </Box>
                )}
              </Paper>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// --- Helpers ---

function cloneRecordSet(rec: Record<string, Set<string>>): Record<string, Set<string>> {
  const out: Record<string, Set<string>> = {};
  Object.entries(rec).forEach(([k, v]) => {
    out[k] = new Set(v);
  });
  return out;
}

function sortAbacCatalog(applications: AbacApplication[]): AbacApplication[] {
  const byDisplayName = <T extends { name: string; title?: string }>(a: T, b: T) =>
    (a.title || a.name).localeCompare(b.title || b.name, undefined, {
      sensitivity: "base",
      numeric: true,
    });

  return applications
    .map((application) => ({
      ...application,
      features: application.features
        .map((feature) => ({
          ...feature,
          attributes: [...feature.attributes].sort(byDisplayName),
        }))
        .sort(byDisplayName),
    }))
    .sort(byDisplayName);
}

export default RoleAbac;
